import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { asOfLabel } from "@/modules/reports/asOf";
import { REPORT_DESCRIPTIONS } from "@/modules/reports/descriptions";
import { useActiveSection } from "@/routes/useActiveSection";

/**
 * The top of every report (#639): its name, one sentence on what it counts,
 * one on when to open it, and on the right the dates it is for and when its
 * figures were read.
 *
 * The words live in `descriptions.ts`, keyed by section, so a report cannot
 * be added without saying what it is for.
 */
export function ReportHeader({
  controls,
  readAt,
  footnote,
  className,
}: {
  /** The report's dates and switches: top right on a desktop, under the text on a phone. */
  controls?: ReactNode;
  /** When the figures on screen were read (`dataUpdatedAt`); absent while loading. */
  readAt?: number;
  /** What the figures count and leave out, in one muted line under the header. */
  footnote?: ReactNode;
  className?: string;
}) {
  const { section } = useActiveSection();
  const words = REPORT_DESCRIPTIONS[section.id];
  // Which day it is, read once: "today" does not need to tick on screen.
  const [now] = useState(() => Date.now());

  return (
    <header
      aria-label={`${section.label} report`}
      className={cn("flex flex-col gap-3 border-b pb-5", className)}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
        <div className="flex max-w-2xl min-w-0 flex-col gap-1.5">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Report
          </p>
          <h2 className="text-2xl leading-tight font-semibold tracking-tight">
            {section.label}
          </h2>
          {words ? (
            <>
              <p className="text-foreground/90 text-sm leading-relaxed">
                {words.what}
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {words.when}
              </p>
            </>
          ) : null}
        </div>

        {controls || readAt ? (
          <div className="flex min-w-0 flex-col gap-2 lg:shrink-0 lg:items-end">
            {controls}
            {readAt ? (
              <p
                className="text-muted-foreground text-xs tabular-nums"
                data-testid="report-as-of"
              >
                {asOfLabel(readAt, now)}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {footnote ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          {footnote}
        </p>
      ) : null}
    </header>
  );
}
